import React from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from 'primereact/dialog';
import ToolIllustrations from './ToolIllustrations';

/**
 * Tool Tutorial Modal
 * Displays visual demonstrations and explanations for each design tool
 */

const toolDescriptions = {
    grid: {
        title: 'Grid Display',
        description: 'Display a grid on the canvas to help align elements precisely. You can customize cell size and color.',
        features: [
            'Customizable grid',
            'Major and minor lines',
            'Size and color control',
            'Quick toggle on/off'
        ],
        shortcut: 'Ctrl/Cmd + Shift + G'
    },
    rulers: {
        title: 'Rulers & Measurements',
        description: 'Display horizontal and vertical rulers with precise measurements to help you determine positions and sizes accurately.',
        features: [
            'Horizontal and vertical rulers',
            'Measurements in pixels or percentage',
            'Major and minor marks',
            'Precise dimension display'
        ],
        shortcut: 'Ctrl/Cmd + Shift + R'
    },
    alignmentGuides: {
        title: 'Alignment Guides',
        description: 'Guide lines that appear automatically when aligning elements with each other or with the canvas center.',
        features: [
            'Automatic alignment lines',
            'Center alignment',
            'Inter-area alignment',
            'Clear visual indicators'
        ],
        shortcut: 'Auto on drag'
    },
    areaNumbers: {
        title: 'Area Numbers Toggle',
        description: 'Show or hide area numbers for a clearer view of content.',
        features: [
            'Hide/show numbers',
            'Improved visibility',
            'Clean preview',
            'Quick toggle'
        ],
        shortcut: 'From tools panel'
    },
    timeline: {
        title: 'Content Timeline',
        description: 'Show or hide the timeline bar that displays content duration.',
        features: [
            'Display content duration',
            'Visual progress bar',
            'Hide/show as needed',
            'Timing preview'
        ],
        shortcut: 'From tools panel'
    },
    outlineMode: {
        title: 'Outline Mode',
        description: 'Display areas without content to focus on layout and dimensions.',
        features: [
            'Content-free view',
            'Focus on layout',
            'Performance improvement',
            'Structure preview'
        ],
        shortcut: 'Ctrl/Cmd + Shift + O'
    },
    colorPicker: {
        title: 'Area Color Picker',
        description: 'Change the color of a specific area to distinguish it or for design purposes.',
        features: [
            'Custom color selection',
            'Comprehensive color palette',
            'Instant preview',
            'Save favorite colors'
        ],
        shortcut: 'Click on area'
    },
    safeZones: {
        title: 'Safe Zones',
        description: 'Display safe zones to prevent content cutoff on different screens.',
        features: [
            'Customizable safe boundaries',
            'Visual warnings',
            'Percentage or pixels',
            'Corner markers'
        ],
        shortcut: 'From tools panel'
    },
    snapToGrid: {
        title: 'Snap to Grid',
        description: 'Elements automatically snap to grid points when dragging or resizing.',
        features: [
            'Automatic snapping',
            'Precise alignment',
            'Easy arrangement',
            'Visual indicator'
        ],
        shortcut: 'Works with Grid'
    },
    backgroundPattern: {
        title: 'Background Pattern',
        description: 'Add decorative patterns to the canvas background to help visualize spacing and alignment. Choose from dots, grids, or line patterns.',
        features: [
            'Multiple pattern options',
            'Dots, grid, and line patterns',
            'Visual spacing guide',
            'Non-intrusive design aid'
        ],
        shortcut: 'From tools panel'
    },
    crosshair: {
        title: 'Crosshair Guide',
        description: 'Display a full-screen crosshair cursor with coordinate display for precise alignment and measurement across the entire canvas.',
        features: [
            'Full-screen guides',
            'Real-time coordinates',
            'Pixel-perfect alignment',
            'Follows mouse cursor'
        ],
        shortcut: 'Ctrl/Cmd + Shift + C'
    },
    focusMode: {
        title: 'Focus Mode',
        description: 'Dim all non-selected areas to focus entirely on the active element. Perfect for working on complex layouts with many areas.',
        features: [
            'Dims distractions',
            'Highlights active area',
            'Adjustable opacity',
            'Improves concentration'
        ],
        shortcut: 'Ctrl/Cmd + Shift + F'
    },
    magnet: {
        title: 'Magnet Snapping',
        description: 'Enable magnetic snapping between adjacent objects. Areas will snap to each other when resized or moved close together.',
        features: [
            'Object-to-object snapping',
            'Gap elimination',
            'Smart alignment',
            'Gapless layouts'
        ],
        shortcut: 'Ctrl/Cmd + Shift + M'
    },
    grayscale: {
        title: 'Contrast Check',
        description: 'Switch the entire view to grayscale to check visual contrast and readability. Ensures your layout works well on all screens.',
        features: [
            'Grayscale preview',
            'Accessibility check',
            'Contrast verification',
            'Readability test'
        ],
        shortcut: 'Ctrl/Cmd + Shift + B'
    },
    areaBorder: {
        title: 'Area Borders',
        description: 'Customize the borders of layout areas. Choose style, width, and color to clearly define area boundaries.',
        features: [
            'Custom border styles',
            'Adjustable width',
            'Color customization',
            'Visual separation'
        ],
        shortcut: 'From tools panel'
    },
    resizeHandles: {
        title: 'Resize Handles',
        description: 'Customize the appearance of area resize handles. Adjust shape, size, and color for better visibility and control.',
        features: [
            'Square or Circle shape',
            'Adjustable size',
            'Custom colors',
            'Professional look'
        ],
        shortcut: 'From tools panel'
    }
};

const ToolTutorialModal = ({ visible, onHide, toolName }) => {
    const { t } = useTranslation();
    if (!toolName || !toolDescriptions[toolName]) return null;

    const tool = toolDescriptions[toolName];
    const IllustrationComponent = ToolIllustrations[toolName];

    return (
        <Dialog
            visible={visible}
            onHide={onHide}
            header={
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                        <i className="bx bx-help-circle text-white text-xl"></i>
                    </div>
                    <div>
                        <h3 className="text-lg font-bold text-gray-800 m-0">{tool.title}</h3>
                        <p className="text-xs text-gray-500 m-0">{t('layouts.designer_visual_guide')}</p>
                    </div>
                </div>
            }
            style={{ width: '600px', maxWidth: '90vw' }}
            modal
            dismissableMask
            className="tool-tutorial-modal"
            pt={{
                root: { className: 'rounded-2xl shadow-2xl' },
                header: { className: 'border-b border-gray-100 pb-4' },
                content: { className: 'p-0' }
            }}
        >
            <div className="p-6">
                {/* Animated Illustration */}
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 mb-6 border border-gray-200">
                    {IllustrationComponent && <IllustrationComponent />}
                </div>

                {/* Description */}
                <div className="mb-6">
                    <h4 className="text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                        <i className="bx bx-info-circle text-indigo-500"></i>
                        {t('layouts.designer_description')}
                    </h4>
                    <p className="text-sm text-gray-600 leading-relaxed bg-indigo-50 p-4 rounded-lg border border-indigo-100">
                        {tool.description}
                    </p>
                </div>

                {/* Features */}
                <div className="mb-6">
                    <h4 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                        <i className="bx bx-list-check text-purple-500"></i>
                        {t('layouts.designer_features')}
                    </h4>
                    <div className="grid grid-cols-2 gap-2">
                        {tool.features.map((feature, index) => (
                            <div 
                                key={index}
                                className="flex items-center gap-2 bg-white p-3 rounded-lg border border-gray-100 hover:border-purple-200 transition-colors"
                            >
                                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-400 to-indigo-500 flex items-center justify-center shrink-0">
                                    <i className="bx bx-check text-white text-xs"></i>
                                </div>
                                <span className="text-xs text-gray-700 font-medium">{feature}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Keyboard Shortcut */}
                {tool.shortcut && (
                    <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-lg p-4 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <i className="bx bx-keyboard text-white text-xl"></i>
                            <span className="text-sm text-gray-300 font-medium">{t('layouts.designer_keyboard_shortcut')}</span>
                        </div>
                        <kbd className="px-3 py-1.5 bg-white/10 border border-white/20 rounded-md text-white text-xs font-mono font-semibold backdrop-blur-sm">
                            {tool.shortcut}
                        </kbd>
                    </div>
                )}

                {/* Close Button */}
                <div className="mt-6 flex justify-end">
                    <button
                        onClick={onHide}
                        className="px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white rounded-lg font-semibold text-sm shadow-lg shadow-indigo-200 transition-all duration-200 flex items-center gap-2"
                    >
                        <span>{t('layouts.designer_got_it')}</span>
                        <i className="bx bx-check-circle"></i>
                    </button>
                </div>
            </div>
        </Dialog>
    );
};

export default ToolTutorialModal;
